import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 2 });
const when = (s) => new Date(s).toLocaleString("en-ZA");

function Status({ t }) {
  const label = t.reversed_by_reference ? `Reversed by ${t.reversed_by_reference}` : t.status === "completed" ? "Completed" : t.status === "failed" ? "Failed" : t.status;
  const cls = t.status === "failed" ? "bg-red-100 text-red-800 hover:bg-red-100" : t.reversed_by_reference ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
  return <Badge className={cls} data-testid={`transfer-status-${t.id}`}>{label}</Badge>;
}

function Detail({ t, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const ref = useRef((window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString());
  const canReverse = t && t.status === "completed" && !t.reversed_by_id && !t.reverses_id;
  const reverse = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/transfers/${t.id}/reverse`, { notes, client_ref: ref.current });
      toast.success(`Reversal ${r.data.reference} recorded`); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Reversal failed — no stock was moved"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="transfer-detail-dialog">
        {t && <>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex flex-wrap items-center gap-2"><span data-testid="transfer-detail-ref">{t.reference}</span><Status t={t} /></DialogTitle>
            <DialogDescription>{t.source_store_name} → {t.dest_store_name} · {when(t.completed_at || t.created_at)} · by {t.created_by_name}</DialogDescription>
          </DialogHeader>
          {t.reverses_id && <div className="text-xs text-amber-700">This is a reversal of an earlier transfer.</div>}
          {t.error && <div className="text-xs text-[#C05746]">Failed: {t.error} — no stock was moved.</div>}
          <div className="border rounded-md divide-y text-sm">
            {t.lines.map((l) => (
              <div key={l.product_id} className="p-2" data-testid={`transfer-detail-line-${l.sku}`}>
                <div className="flex justify-between gap-2"><span className="truncate">{l.name} <span className="font-mono text-[11px] text-muted-foreground">{l.sku}</span></span><b className="font-mono">{fmt(l.quantity)} {l.unit}</b></div>
                <div className="font-mono text-[11px] text-muted-foreground">{t.source_store_name}: {fmt(l.source_before)} → {fmt(l.source_after)} · {t.dest_store_name}: {fmt(l.dest_before)} → {fmt(l.dest_after)}</div>
              </div>
            ))}
          </div>
          {t.notes && <div className="text-xs text-muted-foreground" data-testid="transfer-detail-notes">Notes: {t.notes}</div>}
          {canReverse && <Textarea placeholder="Reason for reversal" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="transfer-reverse-notes" />}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            {canReverse && !sure && <Button variant="outline" className="text-[#C05746]" onClick={() => setSure(true)} data-testid="transfer-reverse-btn">Record reversal</Button>}
            {canReverse && sure && <Button className="bg-[#C05746] hover:bg-[#a94a3b] text-white" disabled={busy} onClick={reverse} data-testid="transfer-reverse-confirm-btn">{busy ? "Reversing…" : "Confirm reversal — move stock back"}</Button>}
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export default function TransferHistory({ transfers, reload }) {
  const [open, setOpen] = useState(null);
  if (!transfers.length) return <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="transfer-history-empty">No transfers yet.</Card>;
  return (
    <div className="space-y-2" data-testid="transfer-history">
      {transfers.map((t) => (
        <Card key={t.id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`transfer-row-${t.reference}`}>
          <div className="text-sm min-w-0">
            <div className="flex flex-wrap items-center gap-2"><b className="font-mono">{t.reference}</b><Status t={t} /></div>
            <div className="text-xs text-muted-foreground mt-1">{when(t.completed_at || t.created_at)} · {t.source_store_name} → {t.dest_store_name} · {t.lines.map((l) => `${l.name} ×${fmt(l.quantity)}`).join(", ") || "—"} · {t.created_by_name}</div>
            {t.notes && <div className="text-xs text-muted-foreground truncate">{t.notes}</div>}
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(t)} data-testid={`transfer-open-${t.reference}`}>View</Button>
        </Card>
      ))}
      <Detail key={open?.id || "none"} t={open} onClose={() => setOpen(null)} onDone={reload} />
    </div>
  );
}
